/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Axios interceptors: attach JWT + X-Session-Id, retry 401 with a fresh token,
 * treat 409 session codes as eviction (not a bad token).
 */

import axios from 'axios';
import { logger } from './logger';
import { setupRequestCache } from './requestCache';
import { clearSessionId, ensureSession, getSessionId, handleEviction } from './sessionManager';

const SESSION_HEADER = 'X-Session-Id';
const CLAIM_PATH = 'session/claim';

/** Codes that mean this browser genuinely lost its session and must sign in again. */
const EVICTION_CODES = ['SESSION_SUPERSEDED', 'SESSION_EXPIRED'];

const isFarelanesApi = (url) => !(url?.startsWith('http') && !url?.includes('myfarelanes.com'));

/**
 * Setup axios interceptors for automatic token injection and error handling
 * This should be called once when the app initializes
 *
 * @param {Function} getTokenFunction - Function that returns a promise with the token
 * @param {Function} claimSessionFunction - Function that claims a device session and resolves its id
 */
export const setupAxiosInterceptors = (getTokenFunction, claimSessionFunction) => {
  // Setup request caching and deduplication FIRST (before token interceptor)
  // This ensures duplicate requests are caught before token injection
  setupRequestCache();

  // Request interceptor: Automatically add token to all requests
  axios.interceptors.request.use(
    async (config) => {
      // Skip token injection if URL is external or doesn't need auth
      if (!isFarelanesApi(config.url)) {
        return config;
      }

      // Skip if Authorization header is already set
      if (config.headers?.Authorization) {
        // Session still applies even when the caller passed its own token.
        await attachSession(config, claimSessionFunction);
        return config;
      }

      try {
        const token = await getTokenFunction();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // If token fetch fails, let the request proceed
        // Individual components can handle the 401 error
        logger.warn('Failed to get token for request:', error);
      }

      await attachSession(config, claimSessionFunction);
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle token expiration and retry
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // A dead session is not a token problem, so it must not reach the 401 refresh-and-retry path.
      if (error.response?.status === 409) {
        const code = error.response?.data?.error;

        if (EVICTION_CODES.includes(code)) {
          handleEviction(code);
          return Promise.reject(error);
        }

        // SESSION_REQUIRED means we sent no session at all, which is a client-state problem rather
        // than an eviction. Re-claim and retry once. Signing the user out here instead would loop
        // forever whenever claiming is broken.
        if (code === 'SESSION_REQUIRED' && originalRequest && !originalRequest._sessionRetry) {
          originalRequest._sessionRetry = true;
          try {
            clearSessionId();
            const sessionId = await ensureSession(claimSessionFunction);
            if (sessionId) {
              originalRequest.headers[SESSION_HEADER] = sessionId;
              return axios(originalRequest);
            }
          } catch (claimError) {
            logger.warn('Could not re-claim device session:', claimError);
          }
          return Promise.reject(error);
        }
      }

      // If error is 401 and we haven't retried yet
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to get a fresh token (force refresh)
          const newToken = await getTokenFunction({ forceRefresh: true });

          // Update the request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          // If refresh fails, reject with original error
          // The app's error boundary or auth guard will handle it
          return Promise.reject(error);
        }
      }

      // For other errors, just reject normally
      return Promise.reject(error);
    }
  );
};

/**
 * Attach the device session id, claiming one first if this browser doesn't have it yet.
 * The claim call itself is excluded or it would wait on its own result.
 */
const attachSession = async (config, claimSessionFunction) => {
  if (config.url?.includes(CLAIM_PATH)) {
    return;
  }
  try {
    let sessionId = getSessionId();
    if (!sessionId && claimSessionFunction) {
      sessionId = await ensureSession(claimSessionFunction);
    }
    if (sessionId) {
      config.headers[SESSION_HEADER] = sessionId;
    }
  } catch (error) {
    // Without a session the API decides what to do; enforcement stays server-side.
    logger.warn('Could not attach device session:', error);
  }
};

/**
 * Reset axios interceptors (useful for testing or cleanup)
 */
export const resetAxiosInterceptors = () => {
  axios.interceptors.request.clear();
  axios.interceptors.response.clear();
};
